import * as Sentry from "@sentry/react";

export type AudioStreamingStopReason =
  | "pause_action"
  | "exit_action"
  | "completed";

export type AudioStreamingTrackingData = {
  streamingDuration: number;
  audioDuration: number;
  bufferedDuration: number;
  receivedChunks: number;
  totalChunks: number;
};

export type AudioStreamingConfig = {
  websocketUrl: string;
  messagePayload: object;
  accessToken: string;
  onChunkReceived?: (chunkNumber: number) => void;
  onPlaybackComplete?: (audioUrl: string | null) => void;
  onPlaybackStarted?: () => void;
  onPlaybackPaused?: () => void;
  onStreamingStarted?: () => void;
  onStreamingStopped?: (
    reason: AudioStreamingStopReason,
    data: AudioStreamingTrackingData
  ) => void;
};

type AudioContextWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

type AudioStreamMessage = {
  type?: string;
  sample_rate?: number;
  channels?: number;
  audio_url?: string;
  data?: string | { audio_url?: string };
  chunk_number?: number;
  total_chunks?: number;
};

const isAudioStreamMessage = (value: unknown): value is AudioStreamMessage =>
  typeof value === "object" && value !== null;

const AudioContextClass =
  typeof window !== "undefined"
    ? window.AudioContext || (window as AudioContextWindow).webkitAudioContext
    : null;

export class AudioStreamingStore {
  private audioContext: AudioContext | null = null;
  private audioChunks: AudioBuffer[] = [];
  private websocket: WebSocket | null = null;
  private isPlaying = false;
  private isPaused = false;
  private bufferedDuration = 0;
  private config: AudioStreamingConfig;
  private isFirstChunk = true;
  private lastChunkNumber = 0;
  private sampleRate = 0;
  private channels = 0;
  private scheduledTime = 0;
  private currentSourceNode: AudioBufferSourceNode | null = null;
  private isStreamComplete = false;
  private lastChunkFinishedTime = 0;
  private completionCheckTimeout: ReturnType<typeof setTimeout> | null = null;

  private streamingStartedAt: Date | null = null;
  private streamingStoppedAt: Date | null = null;
  private receivedChunks = 0;
  private totalChunks = -1;
  private audioUrl: string | null = null;

  constructor(config: AudioStreamingConfig) {
    this.config = config;
  }

  async initialize(): Promise<void> {
    if (!AudioContextClass) return;
    this.audioContext = new AudioContextClass();
    await this.audioContext.resume();
    await this.connectWebSocket();
  }

  private async connectWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.websocket = new WebSocket(this.config.websocketUrl, [
          "access_token",
          this.config.accessToken
        ]);

        this.websocket.onopen = () => {
          const payload = JSON.stringify(this.config.messagePayload);
          this.websocket?.send(payload);
          resolve();
        };

        this.websocket.onmessage = async (event) => {
          await this.handleWebSocketMessage(event.data);
        };

        this.websocket.onerror = (error) => {
          Sentry.captureException(error, {
            tags: { component: "AudioStreaming", detail: "WebSocket error" }
          });
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  private async handleWebSocketMessage(messageData: unknown): Promise<void> {
    try {
      const message =
        typeof messageData === "string"
          ? (JSON.parse(messageData) as AudioStreamMessage)
          : isAudioStreamMessage(messageData)
            ? messageData
            : null;

      if (!message) return;

      switch (message.type) {
        case "audio_start":
          this.sampleRate = message.sample_rate || 0;
          this.channels = message.channels || 0;
          this.audioUrl = message.audio_url || "";
          break;

        case "audio_chunk":
          if (
            typeof message.data === "string" &&
            typeof message.chunk_number === "number"
          ) {
            await this.handleAudioChunk(message.data, message.chunk_number);
          }
          break;

        case "audio_stream_complete":
          this.totalChunks = message.total_chunks || -1;
          this.isStreamComplete = true;
          if (this.isPlaying) this.startCompletionCheck();
          break;

        case "complete":
          this.isStreamComplete = true;
          if (typeof message.data === "object" && message.data !== null) {
            this.audioUrl = message.data.audio_url || this.audioUrl;
          }
          if (this.isPlaying) this.startCompletionCheck();
          break;
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "AudioStreaming", detail: "message handling error" }
      });
    }
  }

  private async handleAudioChunk(
    audioData: string,
    chunkNumber: number
  ): Promise<void> {
    try {
      this.lastChunkNumber = chunkNumber;
      this.receivedChunks++;

      const arrayBuffer = this.base64ToArrayBuffer(audioData);
      const pcm16Data = new Int16Array(arrayBuffer);
      const float32Data = this.pcm16ToFloat32(pcm16Data);

      if (!this.audioContext) return;

      if (this.audioContext.state === "suspended" && !this.isPaused) {
        await this.audioContext.resume();
      }

      const sampleRate = this.sampleRate || 24000;
      const channels = this.channels || 1;
      const audioBuffer = this.audioContext.createBuffer(
        channels,
        float32Data.length,
        sampleRate
      );

      audioBuffer.getChannelData(0).set(float32Data);

      this.audioChunks.push(audioBuffer);
      this.bufferedDuration += audioBuffer.duration;

      const isFirstAudioChunk = this.audioChunks.length === 1;

      if (isFirstAudioChunk) {
        this.trackStreamingStarted();
      }

      if (this.isFirstChunk) {
        this.isFirstChunk = false;
        this.scheduledTime = this.audioContext.currentTime;
      }

      this.scheduleAudioChunk(audioBuffer);

      if (isFirstAudioChunk && !this.isPaused) {
        this.config.onPlaybackStarted?.();
        this.isPlaying = true;
      }

      this.config.onChunkReceived?.(this.lastChunkNumber);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "AudioStreaming", detail: "chunk error" }
      });
    }
  }

  private scheduleAudioChunk(audioBuffer: AudioBuffer): void {
    if (!this.audioContext) return;

    try {
      const sourceNode = this.audioContext.createBufferSource();
      sourceNode.buffer = audioBuffer;
      sourceNode.connect(this.audioContext.destination);

      const currentTime = this.audioContext.currentTime;
      const playTime = Math.max(this.scheduledTime, currentTime);

      sourceNode.start(playTime);
      this.scheduledTime = playTime + audioBuffer.duration;

      this.currentSourceNode = sourceNode;

      sourceNode.onended = () => {
        this.lastChunkFinishedTime = Date.now();
        if (this.isStreamComplete && this.isPlaying) {
          this.startCompletionCheck();
        }
      };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "AudioStreaming", detail: "schedule error" }
      });
    }
  }

  private pcm16ToFloat32(pcm16Data: Int16Array): Float32Array {
    const float32Data = new Float32Array(pcm16Data.length);
    for (let i = 0; i < pcm16Data.length; i++) {
      float32Data[i] = pcm16Data[i] / 32768.0;
    }
    return float32Data;
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const base64Data = base64.replace(/^data:audio\/[^;]+;base64,/, "");
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  async play(): Promise<void> {
    if (!this.audioContext) return;
    await this.audioContext.resume();
    this.isPlaying = true;
    this.isPaused = false;
    this.config.onPlaybackStarted?.();
  }

  async pause(): Promise<void> {
    if (this.audioChunks.length === 0 || this.isPaused) return;
    if (!this.audioContext) return;

    this.isPaused = true;
    this.isPlaying = false;
    await this.audioContext.suspend();
    await this.trackStreamingStopped("pause_action");
    this.config.onPlaybackPaused?.();
  }

  private stop(): void {
    try {
      if (this.currentSourceNode && this.isPlaying) {
        this.currentSourceNode.stop();
      }
    } catch {
      // Source might already be stopped
    }
    this.isPlaying = false;
    this.isPaused = false;
  }

  private startCompletionCheck(): void {
    if (this.completionCheckTimeout) {
      clearTimeout(this.completionCheckTimeout);
    }

    this.completionCheckTimeout = setTimeout(async () => {
      const timeSinceLastChunk = Date.now() - this.lastChunkFinishedTime;

      if (
        timeSinceLastChunk >= 1000 &&
        this.isPlaying &&
        this.isStreamComplete
      ) {
        this.isPlaying = false;
        this.isPaused = false;
        await this.trackStreamingStopped("completed");
        this.config.onPlaybackComplete?.(this.audioUrl);
      }
    }, 1000);
  }

  async dispose(): Promise<void> {
    try {
      if (this.isPlaying && !this.isPaused) {
        await this.trackStreamingStopped("exit_action");
      }
      this.stop();

      if (this.websocket) {
        this.websocket.close();
        this.websocket = null;
      }

      if (this.audioContext) {
        await this.audioContext.close();
        this.audioContext = null;
      }

      if (this.completionCheckTimeout) {
        clearTimeout(this.completionCheckTimeout);
        this.completionCheckTimeout = null;
      }

      this.audioChunks = [];
      this.currentSourceNode = null;
      this.isPlaying = false;
      this.isPaused = false;
      this.isFirstChunk = true;
      this.lastChunkNumber = 0;
      this.bufferedDuration = 0;
      this.scheduledTime = 0;
      this.isStreamComplete = false;
      this.lastChunkFinishedTime = 0;

      this.streamingStartedAt = null;
      this.streamingStoppedAt = null;
      this.receivedChunks = 0;
      this.totalChunks = -1;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "AudioStreaming", detail: "dispose error" }
      });
    }
  }

  private trackStreamingStarted(): void {
    if (this.streamingStartedAt === null) {
      this.streamingStartedAt = new Date();
      this.config.onStreamingStarted?.();
    }
  }

  private async getAudioDuration(): Promise<number> {
    if (!this.audioUrl) return -1;

    try {
      const audio = new Audio(this.audioUrl);
      const duration = await new Promise<number>((resolve, reject) => {
        audio.addEventListener("loadedmetadata", () => resolve(audio.duration));
        audio.addEventListener("error", () => reject());
        setTimeout(() => reject(), 10000);
      });
      return duration;
    } catch {
      return -1;
    }
  }

  private async trackStreamingStopped(
    reason: AudioStreamingStopReason
  ): Promise<void> {
    if (this.streamingStoppedAt === null) {
      this.streamingStoppedAt = new Date();

      let streamingDuration = -1;
      if (this.streamingStartedAt) {
        streamingDuration =
          (this.streamingStoppedAt.getTime() -
            this.streamingStartedAt.getTime()) /
          1000;
      }

      const bufferedDurationAtStop = this.bufferedDuration;
      const receivedChunksAtStop = this.receivedChunks;
      const totalChunksAtStop = this.totalChunks;
      const audioDuration = await this.getAudioDuration();

      this.config.onStreamingStopped?.(reason, {
        streamingDuration,
        audioDuration,
        bufferedDuration: bufferedDurationAtStop,
        receivedChunks: receivedChunksAtStop,
        totalChunks: totalChunksAtStop
      });
    }
  }
}
